/**
 * Tendințele raportului: încasări vs plăți (bare grupate), rezultatul net
 * cumulat (linie) și soldul bancar la finalul fiecărui bucket (linie, cu
 * întreruperi unde nu se încheie niciun extras). Bucket-urile sunt luni,
 * trimestre sau ani, după perioada raportată (`trends.bucketKind`). Câte o axă
 * pe grafic, legendă + tooltip pe fiecare, numai RON.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TrendsSection as TrendsData } from '../../../lib/monthlyReportApi';
import { REPORT_CHART_COLORS, fmtMoney } from '../../../lib/monthlyReportFormat';
import { bucketLabel } from '../../../lib/reportPeriod';
import { CHART_TOOLTIP_LABEL_STYLE, CHART_TOOLTIP_STYLE } from '../../../lib/chartTheme';
import { useI18n } from '../../../contexts/I18nContext';
import { ReportSection } from './ReportSections';

const AXIS = { fill: '#6B7280', fontSize: 11 };
const GRID = '#E5E7EB';

function compact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
}

export function TrendsSection({ trends }: { trends: TrendsData }) {
  const { t, locale } = useI18n();
  const bucketKind = trends.bucketKind ?? 'month';
  const data = trends.months.map((m) => ({
    ...m,
    label: bucketLabel(m.period, bucketKind, locale),
    [t('monthlyReport.trends.inflows')]: m.revenue,
    [t('monthlyReport.trends.outflows')]: m.expenses,
  }));
  const hasBalance = trends.months.some((m) => m.balance !== null);
  const money = (v: number | string | undefined) =>
    typeof v === 'number' ? fmtMoney(v, 'RON', locale) : String(v ?? '');

  return (
    <ReportSection
      title={t('monthlyReport.section.trends')}
      aside={
        <span className="text-sm text-gray-600" title={t('monthlyReport.trends.runwayHint')}>
          {t('monthlyReport.trends.runway')}:{' '}
          <span className="font-semibold text-gray-900">
            {trends.runwayMonths === null
              ? t('monthlyReport.trends.runwayUnknown')
              : t('monthlyReport.trends.runwayValue', { n: trends.runwayMonths })}
          </span>
          {' · '}
          {t('monthlyReport.trends.average3m')}:{' '}
          <span className="tabular-nums">{fmtMoney(trends.averageExpenses3m, 'RON', locale)}</span>
        </span>
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-gray-700">
            {t('monthlyReport.trends.cashflow')}
          </h3>
          <div className="mt-2 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={AXIS}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={12}
                />
                <YAxis
                  tick={AXIS}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={compact}
                  width={44}
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  formatter={(v) => money(v as number)}
                  cursor={{ fill: '#F3F4F6' }}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey={t('monthlyReport.trends.inflows')}
                  fill={REPORT_CHART_COLORS.inflows}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={18}
                />
                <Bar
                  dataKey={t('monthlyReport.trends.outflows')}
                  fill={REPORT_CHART_COLORS.outflows}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={18}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="min-w-0">
          <h3 className="text-sm font-medium text-gray-700">
            {t('monthlyReport.trends.cumulative')}
          </h3>
          <div className="mt-2 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={AXIS}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={12}
                />
                <YAxis
                  tick={AXIS}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={compact}
                  width={44}
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  formatter={(v) => money(v as number)}
                />
                <Line
                  type="monotone"
                  dataKey="cumulativeNet"
                  name={t('monthlyReport.trends.net')}
                  stroke={REPORT_CHART_COLORS.net}
                  strokeWidth={2}
                  dot={{ r: 3, fill: REPORT_CHART_COLORS.net, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="min-w-0">
          <h3 className="text-sm font-medium text-gray-700">{t('monthlyReport.trends.balance')}</h3>
          {hasBalance ? (
            <div className="mt-2 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={AXIS}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={12}
                  />
                  <YAxis
                    tick={AXIS}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={compact}
                    width={44}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                    formatter={(v) => money(v as number)}
                  />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    name={t('monthlyReport.trends.balance')}
                    stroke={REPORT_CHART_COLORS.inflows}
                    strokeWidth={2}
                    dot={{ r: 3, fill: REPORT_CHART_COLORS.inflows, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="mt-2 text-sm text-gray-500">{t('monthlyReport.trends.balanceNone')}</p>
          )}
        </div>
      </div>
    </ReportSection>
  );
}
